import React, { useState } from 'react';
import { RoutingRulesConfig, RoutingRule, createEmptyRule } from '../../../types/qr.types';
import { RuleCard } from './RuleCard';

interface RuleBuilderProps {
  rules: RoutingRulesConfig;
  onChange: (rules: RoutingRulesConfig) => void;
  fallbackUrl: string;
  defaultExpanded?: boolean; // New prop to control default expansion state
  hasGeoRoutingFeature?: boolean; // Whether user has geographic routing feature
}

export function RuleBuilder({ rules, onChange, fallbackUrl, defaultExpanded = true, hasGeoRoutingFeature = false }: RuleBuilderProps) {
  const [expandedRules, setExpandedRules] = useState<Set<string>>(
    new Set(defaultExpanded ? rules.rules.map(r => r.id) : [])
  );

  const handleAddRule = () => {
    const newPriority = rules.rules.length + 1;
    const newRule = createEmptyRule(newPriority);
    // Minimize all existing rules when adding a new one
    setExpandedRules(new Set([newRule.id]));
    onChange({
      ...rules,
      rules: [...rules.rules, newRule],
    });
  };

  const handleToggleRule = (ruleId: string) => {
    setExpandedRules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ruleId)) {
        newSet.delete(ruleId);
      } else {
        newSet.add(ruleId);
      }
      return newSet;
    });
  };

  const handleUpdateRule = (index: number, rule: RoutingRule) => {
    const newRules = [...rules.rules];
    newRules[index] = rule;
    onChange({ ...rules, rules: newRules });
  };

  const handleDeleteRule = (index: number) => {
    const newRules = rules.rules.filter((_, i) => i !== index);
    // Re-assign priorities
    newRules.forEach((rule, i) => {
      rule.priority = i + 1;
    });
    onChange({ ...rules, rules: newRules });
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newRules = [...rules.rules];
    [newRules[index - 1], newRules[index]] = [newRules[index], newRules[index - 1]];
    // Re-assign priorities
    newRules.forEach((rule, i) => {
      rule.priority = i + 1;
    });
    onChange({ ...rules, rules: newRules });
  };

  const handleMoveDown = (index: number) => {
    if (index === rules.rules.length - 1) return;
    const newRules = [...rules.rules];
    [newRules[index], newRules[index + 1]] = [newRules[index + 1], newRules[index]];
    // Re-assign priorities
    newRules.forEach((rule, i) => {
      rule.priority = i + 1;
    });
    onChange({ ...rules, rules: newRules });
  };


  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Routing Rules</h3>
          <p className="text-sm text-gray-500">
            Create conditional rules to redirect users based on time, location, or device.
          </p>
        </div>
        <button
          type="button"
          onClick={handleAddRule}
          className="flex items-center gap-2 px-4 py-2 bg-[--clinqr-lime] text-[--clinqr-navy] rounded-lg border border-[--clinqr-navy]/20 hover:opacity-90 transition-colors font-medium"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Rule
        </button>
      </div>

      {/* Rules list */}
      {rules.rules.length > 0 ? (
        <div className="space-y-3">
          {rules.rules.map((rule, index) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              index={index}
              isFirst={index === 0}
              isLast={index === rules.rules.length - 1}
              onChange={(updated) => handleUpdateRule(index, updated)}
              onDelete={() => handleDeleteRule(index)}
              onMoveUp={() => handleMoveUp(index)}
              onMoveDown={() => handleMoveDown(index)}
              defaultExpanded={expandedRules.has(rule.id)}
              onToggleExpand={() => handleToggleRule(rule.id)}
              hasGeoRoutingFeature={hasGeoRoutingFeature}
            />
          ))}

          {/* Fallback indicator */}
          <div className="border-2 border-dashed border-[--clinqr-border]/10 rounded-lg p-4 bg-[--clinqr-bg-subtle]/30">
            <div className="flex items-center gap-3">
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-[--clinqr-primary] text-white">
                ELSE
              </span>
              <div className="flex-1">
                <p className="font-medium text-[--clinqr-text-primary]">Default Fallback</p>
                <p className="text-sm text-[--clinqr-text-secondary] truncate">
                  {fallbackUrl || 'No fallback URL set'}
                </p>
              </div>
            </div>
            <p className="text-xs text-[--clinqr-text-muted] mt-2">
              When no rules match, users will be redirected to the fallback URL.
            </p>
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-700 mb-2">No routing rules</h4>
          <p className="text-gray-500 mb-4">
            All visitors will be redirected to your fallback URL.
          </p>
          <button
            type="button"
            onClick={handleAddRule}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[--clinqr-lime] text-[--clinqr-navy] rounded-lg border border-[--clinqr-navy]/20 hover:opacity-90 transition-colors font-medium"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create your first rule
          </button>
        </div>
      )}

    </div>
  );
}

export default RuleBuilder;
